import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { playRing, playBlip, isRingEnabled } from '../utils/ring';
import { TOKEN_KEY } from '../api/client';
import { orders as ordersApi } from '../api/endpoints';

const SocketContext = createContext(null);

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

/**
 * Live link to the API.
 *
 * The headline behaviour: a new order arrives over `order:new`, the panel rings
 * and raises a toast, and any page listening through `useOrderEvents` refreshes
 * itself without the operator having to reload.
 */
export function SocketProvider({ children }) {
  const { admin, storeId } = useAuth();
  const toast = useToast();

  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [liveOrders, setLiveOrders] = useState([]);   // the "new orders" tray
  const listenersRef = useRef(new Set());

  /**
   * Sockets are unavailable on some hosts - a serverless platform tears the
   * function down between requests and cannot hold a WebSocket open. When the
   * connection cannot be established we fall back to polling so new orders are
   * still noticed, just a little later.
   */
  const [transport, setTransport] = useState('connecting'); // connecting | socket | polling
  const failuresRef = useRef(0);
  const seenOrderRef = useRef(null);

  /** Pages register here to be told when anything order-shaped changes. */
  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const broadcast = useCallback((event, payload) => {
    listenersRef.current.forEach((fn) => {
      try {
        fn(event, payload);
      } catch (err) {
        console.error('[socket] listener failed', err);
      }
    });
  }, []);

  useEffect(() => {
    if (!admin) return undefined;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return undefined;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1500,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setTransport('socket');
      failuresRef.current = 0;
      // A store account listens on its own branch room too.
      if (storeId) socket.emit('store:join', storeId);
    });

    socket.on('disconnect', () => setConnected(false));

    socket.on('connect_error', (err) => {
      setConnected(false);
      failuresRef.current += 1;
      // A few failures is a blip; a run of them means this host has no sockets.
      if (failuresRef.current >= 3) {
        setTransport('polling');
        socket.close();
      }
      console.warn('[socket] connect_error:', err.message);
    });

    socket.on('order:new', (order) => {
      if (isRingEnabled()) playRing();
      setLiveOrders((list) => [order, ...list].slice(0, 25));
      toast.success(
        `New order ${order.orderNumber}`,
        `${order.customerName || 'Customer'} - ${order.total} ${order.type === 'walk-in' ? '(walk-in)' : ''}`
      );
      broadcast('order:new', order);
    });

    socket.on('order:updated', (order) => broadcast('order:updated', order));
    // The store published the price; any panel showing this order should refresh.
    socket.on('order:billed', (order) => broadcast('order:billed', order));

    socket.on('order:cancelled', (order) => {
      toast.error(`Order ${order.orderNumber} cancelled`);
      broadcast('order:cancelled', order);
    });

    socket.on('order:paid', (order) => {
      if (isRingEnabled()) playBlip();
      broadcast('order:paid', order);
    });

    socket.on('payment:received', (p) => {
      toast.success(`Payment received`, `${p.orderNumber} - ${p.amount}`);
      broadcast('payment:received', p);
    });

    socket.on('order:partner-rejected', (p) => {
      toast.error('Partner rejected an order', `${p.orderNumber} is back in dispatch`);
      broadcast('order:partner-rejected', p);
    });

    socket.on('partner:kyc-submitted', (p) => {
      toast.info('New partner application', `${p.name || p.mobile} submitted documents`);
      broadcast('partner:kyc-submitted', p);
    });

    socket.on('partner:location', (p) => broadcast('partner:location', p));
    socket.on('partner:status', (p) => broadcast('partner:status', p));

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
    // toast/broadcast are stable; re-connecting on every render would thrash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, storeId]);

  /**
   * Polling fallback.
   *
   * Only runs when the socket could not connect. It watches the newest order
   * id; anything newer than the last one seen is treated exactly like an
   * `order:new` push - same ring, same toast, same listener broadcast.
   */
  useEffect(() => {
    if (!admin || transport !== 'polling') return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await ordersApi.list({ limit: 5 });
        const rows = res.data.orders || [];
        if (cancelled || rows.length === 0) return;

        // The first poll only establishes a baseline - it must not ring for
        // orders that were already there before the panel opened.
        if (seenOrderRef.current === null) {
          seenOrderRef.current = rows[0]._id;
          return;
        }

        const seenIndex = rows.findIndex((o) => o._id === seenOrderRef.current);
        const fresh = seenIndex === -1 ? rows.slice(0, 1) : rows.slice(0, seenIndex);
        if (fresh.length === 0) return;

        seenOrderRef.current = rows[0]._id;
        if (isRingEnabled()) playRing();

        fresh.reverse().forEach((order) => {
          setLiveOrders((list) => [order, ...list].slice(0, 25));
          toast.success(
            `New order ${order.orderNumber}`,
            `${order.customerName || 'Customer'} - ${order.total}`
          );
          broadcast('order:new', order);
        });
      } catch (err) {
        // A failed poll is not worth surfacing; the next tick retries.
      }
    };

    poll();
    const timer = setInterval(poll, 20000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, transport]);

  const clearLiveOrders = useCallback(() => setLiveOrders([]), []);

  const value = useMemo(
    () => ({ socket: socketRef.current, connected, transport, liveOrders, clearLiveOrders, subscribe }),
    [connected, transport, liveOrders, clearLiveOrders, subscribe]
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used inside <SocketProvider>');
  return ctx;
};

/**
 * Convenience hook for pages: `useOrderEvents(() => reload())` re-runs the
 * callback whenever an order event lands.
 */
export function useOrderEvents(handler) {
  const { subscribe } = useSocket();
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => subscribe((event, payload) => ref.current(event, payload)), [subscribe]);
}
